// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// The board: generated from the round seed, rotationally symmetric, and carrying a PRECOMPUTED
// visibility table that every other file in the game reads instead of tracing a line.
//
// ── TERRAIN IS TWO INDEPENDENT BITS ─────────────────────────────────────────────────────────────
// "Blocks feet" and "blocks sight" are separate questions, and keeping them separate is the whole
// cover system:
//
//   OPEN   feet pass · sight passes
//   WALL   feet stop · sight stops    — the thing you hide behind
//   RAIL   feet stop · sight passes   — a barrier you can see over and cannot cross
//
// RAIL is what stops the board being a set of sealed rooms. It shapes where you can WALK without
// changing what you can SEE, so a route that looks safe on the tiles is not safe on the lines, and
// the two questions a player asks — "can I get there" and "will they see me" — stop having the same
// answer. There is no cover percentage and no hit chance anywhere in this game: safety is not being
// on a line, and every line is drawable, drawn, and precomputed here.
//
// ── WHY THE TABLE ───────────────────────────────────────────────────────────────────────────────
// 192 tiles means 192x192 = 36,864 sight questions, which is 4.6KB as a bitset and about four
// milliseconds to fill. After that every line-of-sight query in the game, in the threat overlay
// redrawn under a moving thumb, and in every one of the bot's thousands of rollouts, is a single bit
// lookup. The bot's search budget on a phone exists because of this table.
//
// ── WHY 180-DEGREE ROTATION AND NOT A MIRROR ────────────────────────────────────────────────────
// Both sides must inherit the same geometry or the map decides the match before anybody plans (this
// fleet has shipped a territory game where one seat opened with eight tiles and the other with one).
// A mirror would do that too — but a mirror makes the halves HANDED: a corner you peek round your
// left shoulder is peeked round the right by your opponent, and every learned angle has to be
// relearned flipped. A 180-degree rotation maps (x, y) -> (W-1-x, H-1-y): their position is your
// position seen upside down. Turn the phone round and it is the same board.

import { makeRng, randInt, type Rng } from '@ben-gy/game-engine/rng';
import type { Mode } from './modes';

export const W = 12;
export const H = 16;
export const N = W * H;

export type Cell = 0 | 1 | 2;
export const OPEN: Cell = 0;
export const WALL: Cell = 1;
export const RAIL: Cell = 2;

/** How far outside the mast's own 2x2 block a unit still counts as holding it. */
export const MAST_FIELD = 1;

/**
 * The masts standing on a given turn, each as the top-left tile of its 2x2 block.
 *
 * ── WHY A ROVING MAST IS A PAIR AND NOT A SEQUENCE ──────────────────────────────────────────────
 * The centre block is the one position on an even-sided board that is its OWN rotation, so it is the
 * only single place a fair objective can stand. The obvious way to make it rove is therefore to
 * alternate: a block in their half, then its mirror in yours, for the same number of turns each. On
 * paper that is fair. Measured, it was 0% against 83%.
 *
 * The reason is that "the same number of turns each" is a promise about a match that runs to the
 * turn cap, and most matches do not: they end when somebody banks the target, which happened on turn
 * three, which was in the middle of the first away phase. Whoever the mast was near at that moment
 * won essentially every game. The fairness was real and the match never got far enough to collect
 * it — which is the exact shape of a bug no rules test can see and a hundred seeds report instantly.
 *
 * So a roving mast is a rotational PAIR that stands at the same time: one in each half, always. It
 * is symmetric at every instant rather than on average, so no truncation can bias it, and it is a
 * better mechanic besides — two places to be and two units to split between them, with neither side
 * banking anything while both are occupied.
 */
export function mastsFor(mode: { roving: number }, turn: number): number[] {
  const centre = idx(W / 2 - 1, H / 2 - 1);
  if (!mode.roving) return [centre];
  // TWO positions, alternating — not three. Every mast a mode can ever raise has to be kept clear at
  // generation time, and a third position was another sixteen tiles of guaranteed open ground: with
  // five fields cleared, EVERY roving map came out at a lane score above its own band and generation
  // fell back on 100% of seeds. The maps were wide open, fights were decided on contact, and matches
  // ended in 1.6 turns. Nothing about that was visible in a screenshot or a rules test — it took
  // counting how often the generator gave up.
  const phase = Math.floor(turn / mode.roving) % 2;
  if (phase === 0) return [centre];
  const a = idx(2, 4);
  return [a, idx(W - 2 - tx(a), H - 2 - ty(a))];
}

/** Every mast a mode will ever raise — what generation has to keep clear and reachable. */
export function mastsOf(mode: { roving: number; turns: number }): number[] {
  const out = new Set<number>();
  for (let t = 0; t < mode.turns; t++) for (const m of mastsFor(mode, t)) out.add(m);
  return [...out];
}

/** The tiles that count as holding a mast: its 2x2 block, grown by MAST_FIELD. */
export function mastField(mast: number): number[] {
  const out: number[] = [];
  for (let y = ty(mast) - MAST_FIELD; y <= ty(mast) + 1 + MAST_FIELD; y++) {
    for (let x = tx(mast) - MAST_FIELD; x <= tx(mast) + 1 + MAST_FIELD; x++) {
      if (inBounds(x, y)) out.push(idx(x, y));
    }
  }
  return out;
}

export interface GameMap {
  cells: Uint8Array;
  /** N*N bits: can the centre of tile a see the centre of tile b? */
  vis: Uint8Array;
  /** Spawn tiles, side 0 (bottom) first. */
  spawns: [number[], number[]];
  seed: number;
}

export const idx = (x: number, y: number): number => y * W + x;
export const tx = (i: number): number => i % W;
export const ty = (i: number): number => (i / W) | 0;
export const inBounds = (x: number, y: number): boolean => x >= 0 && y >= 0 && x < W && y < H;
/** The same tile, seen from the other chair. */
export const rotIdx = (i: number): number => (H - 1 - ty(i)) * W + (W - 1 - tx(i));

export const cellAt = (m: GameMap, x: number, y: number): Cell => (inBounds(x, y) ? (m.cells[idx(x, y)] as Cell) : WALL);
export const blocksFeet = (m: GameMap, x: number, y: number): boolean => cellAt(m, x, y) !== OPEN;
export const blocksSight = (m: GameMap, x: number, y: number): boolean => cellAt(m, x, y) === WALL;

/** Is a position (in tile space, so fractional) inside one mast's scoring field? */
export const inMastField = (mast: number, x: number, y: number): boolean => {
  const fx = Math.floor(x);
  const fy = Math.floor(y);
  return (
    fx >= tx(mast) - MAST_FIELD &&
    fx <= tx(mast) + 1 + MAST_FIELD &&
    fy >= ty(mast) - MAST_FIELD &&
    fy <= ty(mast) + 1 + MAST_FIELD
  );
};

/** …or inside any of the masts standing this turn. */
export const inAnyMastField = (masts: number[], x: number, y: number): boolean =>
  masts.some((m) => inMastField(m, x, y));

// ── visibility ──────────────────────────────────────────────────────────────────────────────────

/** A single bit lookup. Every sight question in the game comes through here. */
export function canSeeTile(m: GameMap, a: number, b: number): boolean {
  const bit = a * N + b;
  return (m.vis[bit >> 3] & (1 << (bit & 7))) !== 0;
}

/**
 * Is the line between two tile centres clear of sight-blocking tiles?
 *
 * Sampled along the segment rather than walked as a DDA. A walk that only visits the tiles a
 * mathematical ray passes through lets a line squeeze through the diagonal join of two walls — the
 * corner-peek every grid shooter has, invisible in a screenshot because the line looks perfectly
 * plausible. Sampling at a quarter-tile closes it: any join tight enough to shoot through is tight
 * enough for a sample to land inside one of the two walls.
 */
function traceClear(cells: Uint8Array, a: number, b: number): boolean {
  const ax = tx(a) + 0.5;
  const ay = ty(a) + 0.5;
  const bx = tx(b) + 0.5;
  const by = ty(b) + 0.5;
  const dx = bx - ax;
  const dy = by - ay;
  const steps = Math.ceil(Math.hypot(dx, dy) * 4);
  for (let s = 1; s < steps; s++) {
    const t = s / steps;
    const x = Math.floor(ax + dx * t);
    const y = Math.floor(ay + dy * t);
    if (x === tx(a) && y === ty(a)) continue;
    if (x === tx(b) && y === ty(b)) continue;
    if (cells[idx(x, y)] === WALL) return false;
  }
  return true;
}

/**
 * Fill the table. Symmetric by construction — sight is mutual, and computing only the upper triangle
 * makes it impossible for the two directions to ever disagree, which would be a bug where one unit
 * can shoot another that cannot shoot back.
 */
export function buildVis(cells: Uint8Array): Uint8Array {
  const vis = new Uint8Array((N * N) >> 3);
  const set = (a: number, b: number): void => {
    const bit = a * N + b;
    vis[bit >> 3] |= 1 << (bit & 7);
  };
  for (let a = 0; a < N; a++) {
    set(a, a);
    for (let b = a + 1; b < N; b++) {
      if (!traceClear(cells, a, b)) continue;
      set(a, b);
      set(b, a);
    }
  }
  return vis;
}

// ── generation ──────────────────────────────────────────────────────────────────────────────────

function bordered(): Uint8Array {
  const cells = new Uint8Array(N);
  for (let x = 0; x < W; x++) {
    cells[idx(x, 0)] = WALL;
    cells[idx(x, H - 1)] = WALL;
  }
  for (let y = 0; y < H; y++) {
    cells[idx(0, y)] = WALL;
    cells[idx(W - 1, y)] = WALL;
  }
  return cells;
}

/** Stamp a block and its rotation at once, so the board can never be asymmetric by construction. */
function stamp(cells: Uint8Array, x: number, y: number, w: number, h: number, kind: Cell): void {
  for (let j = 0; j < h; j++) {
    for (let i = 0; i < w; i++) {
      const cx = x + i;
      const cy = y + j;
      if (!inBounds(cx, cy)) continue;
      cells[idx(cx, cy)] = kind;
      cells[rotIdx(idx(cx, cy))] = kind;
    }
  }
}

const SPAWN_COLS: Record<number, number[]> = { 2: [3, 8], 3: [2, 5, 9], 4: [2, 4, 7, 9] };

function spawnsFor(units: number): [number[], number[]] {
  const cols = SPAWN_COLS[units] ?? SPAWN_COLS[3];
  const a = cols.map((x) => idx(x, H - 2));
  return [a, a.map(rotIdx)];
}

function reachable(cells: Uint8Array, from: number): Uint8Array {
  const seen = new Uint8Array(N);
  const stack = [from];
  seen[from] = 1;
  while (stack.length) {
    const p = stack.pop() as number;
    const x = tx(p);
    const y = ty(p);
    for (const [dx, dy] of [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
    ]) {
      const nx = x + dx;
      const ny = y + dy;
      if (!inBounds(nx, ny) || cells[idx(nx, ny)] !== OPEN || seen[idx(nx, ny)]) continue;
      seen[idx(nx, ny)] = 1;
      stack.push(idx(nx, ny));
    }
  }
  return seen;
}

/**
 * How much of the board can see how much of the board, over pairs within firing range.
 *
 * This is the number that decides whether a map is a game. Too low and every fight is at knife
 * range, so a plan is guesswork; too high and both sides sit still and nothing can move. Generation
 * rejects until it lands inside the mode's band, which makes "is this map any good" a search over
 * seeds rather than an argument.
 */
export function laneScore(m: GameMap): number {
  let seen = 0;
  let pairs = 0;
  for (let a = 0; a < N; a++) {
    if (m.cells[a] !== OPEN) continue;
    for (let b = a + 1; b < N; b++) {
      if (m.cells[b] !== OPEN) continue;
      const d = Math.hypot(tx(a) - tx(b), ty(a) - ty(b));
      if (d > 9) continue;
      pairs++;
      if (canSeeTile(m, a, b)) seen++;
    }
  }
  return pairs === 0 ? 0 : seen / pairs;
}

function carve(rng: Rng, mode: Mode): Uint8Array {
  const cells = bordered();
  // Blocks are stamped in the TOP half and rotated into the bottom, so fairness is structural.
  for (let n = 0; n < mode.blocks; n++) {
    // Small blocks, deliberately. The first cut allowed 4x4 and, doubled by the rotation and added
    // to the border ring, more than half the board came out solid — walls stopped reading as objects
    // you hide behind and started reading as the background, with the open ground as the shapes.
    const w = randInt(rng, 1, 3);
    const h = randInt(rng, 1, 3);
    const x = randInt(rng, 1, W - w);
    const y = randInt(rng, 2, Math.floor(H / 2));
    stamp(cells, x, y, w, h, WALL);
  }
  for (let n = 0; n < mode.rails; n++) {
    const horiz = randInt(rng, 0, 2) === 0;
    const len = randInt(rng, 2, 5);
    const x = randInt(rng, 1, W - (horiz ? len : 1));
    const y = randInt(rng, 2, Math.floor(H / 2));
    stamp(cells, x, y, horiz ? len : 1, horiz ? 1 : len, RAIL);
  }

  return cells;
}

function acceptable(m: GameMap, mode: Mode): boolean {
  let open = 0;
  for (let i = 0; i < N; i++) if (m.cells[i] === OPEN) open++;
  // Everything walkable has to be walkable FROM everywhere — a sealed pocket is a unit ordered into
  // a place it can never leave.
  const seen = reachable(m.cells, m.spawns[0][0]);
  let count = 0;
  for (let i = 0; i < N; i++) count += seen[i];
  if (count !== open) return false;
  for (const side of m.spawns) for (const p of side) if (!seen[p]) return false;
  // Every mast this mode will ever raise has to be reachable, or Roving's third turn is a turn
  // nobody can contest and nobody was warned about.
  for (const mast of mastsOf(mode)) for (const t of mastField(mast)) if (!seen[t]) return false;

  // Nobody may be shootable on the first tick of the first turn. `sealSpawnLines` has already broken
  // any such line, so this is the check that the repair actually worked rather than the check that
  // the seed happened to be kind.
  for (const a of m.spawns[0]) for (const b of m.spawns[1]) if (canSeeTile(m, a, b)) return false;

  const lanes = laneScore(m);
  return lanes >= mode.lanes[0] && lanes <= mode.lanes[1];
}

/**
 * VERIFY AND REPAIR, never "unreachable by construction".
 *
 * Nobody may be shootable on the first tick of the first turn: spawns are fourteen rows apart
 * against thirteen tiles of sight, so it ought to be free — and on a handful of seeds in every
 * thousand it is not, because a straight corridor happens to open between two spawn tiles. Rejecting
 * those seeds would be fine until the day every candidate fails and the fallback ships one anyway,
 * which is exactly how a construction argument becomes a bug nobody looks for. So the offending line
 * is BROKEN instead: a wall is stamped (with its rotation, so the board stays symmetric) at the
 * midpoint of the line, and the table is rebuilt.
 */
function sealSpawnLines(cells: Uint8Array, spawns: [number[], number[]], protectedTiles: Set<number>): Uint8Array {
  let vis = buildVis(cells);
  for (let pass = 0; pass < 8; pass++) {
    let dirty = false;
    for (const a of spawns[0]) {
      for (const b of spawns[1]) {
        const bit = a * N + b;
        if ((vis[bit >> 3] & (1 << (bit & 7))) === 0) continue;
        // Walk the line and take the first tile that is safe to build on — never a mast field, never
        // a spawn, and never something whose rotation is one of those either, because every stamp is
        // mirrored. The line spans most of the board, so there is always somewhere.
        const steps = 24;
        for (let s = 6; s < steps - 5; s++) {
          const t = s / steps;
          const x = Math.round(tx(a) + (tx(b) - tx(a)) * t);
          const y = Math.round(ty(a) + (ty(b) - ty(a)) * t);
          if (x < 1 || y < 1 || x >= W - 1 || y >= H - 1) continue;
          const here = idx(x, y);
          if (protectedTiles.has(here) || protectedTiles.has(rotIdx(here))) continue;
          stamp(cells, x, y, 1, 1, WALL);
          dirty = true;
          break;
        }
      }
    }
    if (!dirty) return vis;
    vis = buildVis(cells);
  }
  return vis;
}

function assemble(seed: number, mode: Mode): GameMap {
  const cells = carve(makeRng(seed), mode);
  const spawns = spawnsFor(mode.units);
  // Everything that must stay open, cleared BEFORE the seal so the seal cannot be undone by it.
  const keep = new Set<number>();
  for (const mast of mastsOf(mode)) for (const t of mastField(mast)) keep.add(t);
  for (const side of spawns) {
    for (const p of side) {
      keep.add(p);
      keep.add(ty(p) > H / 2 ? p - W : p + W);
    }
  }
  for (const t of keep) cells[t] = OPEN;
  const vis = sealSpawnLines(cells, spawns, keep);
  return { cells, vis, spawns, seed };
}

/**
 * The map for a round: deterministic in (seed, mode), so both peers build the same bytes and nothing
 * about the board is ever transmitted.
 *
 * Generation is a rejection search — up to 48 candidates, then the closest-to-acceptable one — so a
 * caller can never be handed a disconnected or degenerate board. The fallback is what makes this
 * function TOTAL: a generator that can throw will throw, on somebody's phone, on a seed nobody
 * tested. "Unreachable by construction" is a hypothesis; this verifies and repairs instead.
 */
export function makeMap(seed: number, mode: Mode): GameMap {
  let best: GameMap | null = null;
  let bestGap = Infinity;
  const mid = (mode.lanes[0] + mode.lanes[1]) / 2;
  for (let attempt = 0; attempt < 48; attempt++) {
    const s = (seed + attempt * 0x9e3779b1) >>> 0;
    const m = assemble(s, mode);
    if (acceptable(m, mode)) return m;
    let open = 0;
    for (let i = 0; i < N; i++) if (m.cells[i] === OPEN) open++;
    const seen = reachable(m.cells, m.spawns[0][0]);
    let count = 0;
    for (let i = 0; i < N; i++) count += seen[i];

    // THE FALLBACK IS NOT ALLOWED TO BREAK THE ONE RULE THAT DECIDES TURN ONE. A candidate where two
    // spawns can see each other is never eligible, however good its lane score — a unit that can be
    // shot before anybody has made a decision is a coin flip, not an opening. When the walls got
    // thinner this stopped being hypothetical: `sealSpawnLines` can fail to find an unprotected tile
    // on the line, the candidate is correctly rejected, and the least-bad fallback then shipped the
    // violation anyway on one seed in forty. Ranking it as INELIGIBLE rather than merely worse is
    // what makes the property hold on every seed instead of almost all of them.
    let eligible = count === open;
    if (eligible) {
      for (const a of m.spawns[0]) for (const b of m.spawns[1]) if (canSeeTile(m, a, b)) eligible = false;
    }
    const gap = eligible ? Math.abs(laneScore(m) - mid) : 100 + attempt;
    if (gap < bestGap) {
      bestGap = gap;
      best = m;
    }
  }
  return best as GameMap;
}

/** Every walkable tile — the bot's candidate space, and the tests' sample space. */
export function openTiles(m: GameMap): number[] {
  const out: number[] = [];
  for (let i = 0; i < N; i++) if (m.cells[i] === OPEN) out.push(i);
  return out;
}

/**
 * Walking distance from one tile to every other, by breadth-first search over open tiles.
 *
 * Used for the bot's candidate routing and for "how far from the zone is this tile" scoring. Plain
 * BFS on the 4-neighbourhood: paths in this game are straight legs between waypoints, so this is a
 * ranking heuristic rather than the movement rule, and it is deliberately not asked to be one.
 */
export function distanceField(m: GameMap, from: number): Int16Array {
  const d = new Int16Array(N).fill(-1);
  const q = [from];
  d[from] = 0;
  for (let head = 0; head < q.length; head++) {
    const p = q[head];
    const x = tx(p);
    const y = ty(p);
    for (const [dx, dy] of [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
    ]) {
      const nx = x + dx;
      const ny = y + dy;
      if (!inBounds(nx, ny)) continue;
      const j = idx(nx, ny);
      if (m.cells[j] !== OPEN || d[j] >= 0) continue;
      d[j] = d[p] + 1;
      q.push(j);
    }
  }
  return d;
}
